import { MAILS } from '../data';
import type { BoxMail } from '../data';

// Logic màn Mint-Box (chỉ đọc): nhóm ngày, lọc, SLA, thẻ job. Sắp xếp / nhóm chép từ script board (dayOf, tsOf).

export type Day = 'today' | 'yday' | 'week';
export type MailFilter = 'all' | 'open' | 'unread' | 'mine';

export const DAY_LABEL: Readonly<Record<Day, string>> = { today: 'HÔM NAY', yday: 'HÔM QUA', week: 'TUẦN NÀY' };
export const VIEWER = 'DAVID';
export const UNASSIGNED = 'UNASSIGNED';

export function dayOf(m: BoxMail): Day {
  return m.time === 'Hôm qua' ? 'yday' : /^\d\d\/\w{3}$/.test(m.time) ? 'week' : 'today';
}

/** Mốc sắp xếp: hôm nay > hôm qua > tuần này, rồi giờ, rồi id (thư mới hơn trước). */
export function tsOf(m: BoxMail): number {
  const d = dayOf(m);
  const mm = /^(\d\d):(\d\d)$/.exec(m.time);
  const base = mm ? Number(mm[1]) * 60 + Number(mm[2]) : 600;
  return (d === 'today' ? 10000 : d === 'yday' ? 5000 : 0) + base + m.id / 100;
}

export function filterMails(mails: readonly BoxMail[], f: MailFilter, q = ''): BoxMail[] {
  const needle = q.trim().toLowerCase();
  return mails
    .filter((m) => {
      if (f !== 'unread' && f !== 'mine') return !m.done; // "Tất cả" ẩn thư đã xử lý như board (showDone tắt)
      if (f === 'unread') return !!m.unread;
      if (f === 'mine') return m.pic === VIEWER;
      return true;
    })
    .filter((m) => !needle || `${m.from} ${m.subj} ${m.job} ${m.bk} ${m.kh}`.toLowerCase().includes(needle))
    .sort((a, b) => tsOf(b) - tsOf(a));
}

export interface MailGroup {
  day: Day;
  label: string;
  rows: BoxMail[];
}

export function groupByDay(mails: readonly BoxMail[]): MailGroup[] {
  const out: MailGroup[] = [];
  for (const m of mails) {
    const day = dayOf(m);
    const last = out[out.length - 1];
    if (last && last.day === day) last.rows.push(m);
    else out.push({ day, label: DAY_LABEL[day], rows: [m] });
  }
  return out;
}

/** Số thư chưa gán PIC trong danh sách (chưa xử lý). */
export function unassignedCount(mails: readonly BoxMail[]): number {
  return mails.filter((m) => m.pic === UNASSIGNED && !m.done).length;
}

/** `26′` hoặc `−7′` khi quá hạn; null = không áp SLA. */
export function slaShort(m: BoxMail): string | null {
  return m.sla === null ? null : `${m.sla}′`;
}

export function slaLabel(m: BoxMail): string | null {
  if (m.sla === null) return null;
  return m.sla < 0 ? `Quá SLA ${-m.sla} phút` : `Còn ${m.sla} phút`;
}

/** Tên hộp trên đầu danh sách: `info@`. */
export const boxTitle = (box: string): string => `${box}@`;

/** Hai chữ đầu của username làm avatar PIC ("PHUONGNKT" → "PH", "PhuongHT" → "Ph"). */
export const initials = (u: string): string => u.slice(0, 2);

export const attSizeKb = (mb: number): string => (mb < 1 ? `${Math.round(mb * 1000)} KB` : `${mb.toLocaleString('vi-VN')} MB`);

/** Tổng dung lượng tệp đính kèm của thư (KB / MB). */
export function attTotal(m: BoxMail, mbOf: Readonly<Record<string, number>>): string {
  return attSizeKb(m.att.reduce((s, a) => s + (mbOf[a.name] ?? 0.1), 0));
}

// Thẻ job ở bảng phải: board dùng một thẻ mẫu cho mọi job (trạng thái theo bảng JOBS, các dòng cố định).
const JOB_STATUS: Readonly<Record<string, string>> = {
  SE260639142: 'BK Confirmed',
  SE260639118: 'BK Standby',
  SE260639130: 'BK Confirmed',
  SE260639112: 'BK Confirmed',
  SE260639121: 'BK Confirmed',
  SE260639103: 'BK Confirmed',
  SE260639109: 'BL Wait RLS',
};

export interface JobCard {
  no: string;
  status: string;
  rows: ReadonlyArray<readonly [string, string]>;
  thread: ReadonlyArray<readonly [string, string]>;
}

export function jobCard(no: string): JobCard {
  return {
    no,
    status: JOB_STATUS[no] ?? 'BK Confirmed',
    rows: [
      ['Khách', 'RUITONGVN-SC'],
      ['Tuyến', 'CLI → LAX'],
      ['Tàu', 'YM CERTAINTY 089N'],
      ['ETD', '24/Oct/2026 11:00'],
      ['Cut-off SI', '22/Oct/2026 16:00'],
      ['BKG chủ job', 'PhuongHT (backup TranTPB)'],
      ['CS / Sale', 'MAITTN / DAVID'],
    ],
    thread: [
      ['Booking Confirmation YML', '08:42'],
      ['SI draft từ khách', '21/Oct'],
      ['RQ booking 2×40HC', '20/Oct'],
    ],
  };
}

/** Hộp thư đến của nhóm (info@): mọi thư chưa vào thư mục đặc biệt. */
export const inboxOf = (box: BoxMail['box'], mails: readonly BoxMail[] = MAILS): BoxMail[] => mails.filter((m) => m.box === box && !m.folder);

// ---- Cột hộp thư (khổ ≥ 1800): đếm theo board `Mint-Box-1920` ----

export interface BoxCounts {
  all: number;
  mine: number;
  /** thư chưa đọc theo hộp chung */
  boxes: Readonly<Record<'info' | 'support' | 'booking' | 'trucking' | 'billing', number>>;
  /** info@: thư chưa gán PIC (chip "3 chưa gán") */
  infoUnassigned: number;
  folders: { inbox: number; snz: number; junk: number };
  smart: { open: number; unassigned: number; late: number; mine: number; watch: number; hasJob: number; done: number };
  labels: ReadonlyArray<readonly [string, number]>;
}

export const LABELS = ['BOOKING', 'URGENT_CASE', 'LICH_TAU', 'DnD_PWC', 'NOTICE', 'CHUNG_TU'] as const;

/** Số đếm của hộp đang xem (info@). "Tất cả hộp thư" / hộp = số thư CHƯA ĐỌC ở hộp thư đến. */
export function boxCounts(box: BoxMail['box'] = 'info', mails: readonly BoxMail[] = MAILS): BoxCounts {
  const unread = (k: BoxMail['box']) => mails.filter((m) => m.box === k && !m.folder && m.unread).length;
  const inb = inboxOf(box, mails);
  return {
    all: mails.filter((m) => !m.folder && m.unread).length,
    mine: unread('mine'),
    boxes: { info: unread('info'), support: unread('support'), booking: unread('booking'), trucking: unread('trucking'), billing: unread('billing') },
    infoUnassigned: unassignedCount(inb),
    folders: {
      inbox: inb.filter((m) => m.unread).length,
      snz: mails.filter((m) => m.box === box && m.folder === 'snz').length,
      junk: mails.filter((m) => m.box === box && m.folder === 'junk').length,
    },
    smart: {
      open: inb.filter((m) => !m.done).length,
      unassigned: unassignedCount(inb),
      late: inb.filter((m) => (m.sla ?? 0) < 0).length,
      mine: inb.filter((m) => m.pic === VIEWER).length,
      watch: inb.filter((m) => m.watch).length,
      hasJob: inb.filter((m) => m.job).length,
      done: inb.filter((m) => m.done).length,
    },
    labels: LABELS.map((k) => [k, inb.filter((m) => m.label === k).length] as const),
  };
}

/** Dung lượng hộp thư (mẫu như board). */
export const QUOTA = { used: '3,15 GB' } as const;

/** Dòng xem trước của thư (board iPad / Phone): bỏ lời chào đầu, nối các dòng còn lại, tối đa 160 ký tự. */
export function preview(m: BoxMail): string {
  const lines = m.body.map((x) => x.trim()).filter(Boolean);
  const rest = lines.length > 1 && /^(dear|kính gửi|kính chào|chào|hi|hello|gửi)\b/i.test(lines[0] ?? '') ? lines.slice(1) : lines;
  return rest.join(' ').replace(/\s+/g, ' ').slice(0, 160);
}
